import { STEPS_PER_BAR, type AudioClip, type Instrument, type Pattern, type Project, type SectionKind } from '../domain/schemas';

/**
 * Converts project data into a flat, sorted list of timed note events.
 *
 * This is the single source of truth for *what plays when*: realtime playback and
 * offline WAV rendering schedule exactly these events, so the two cannot drift apart.
 * Times are in transport ticks (PPQ 192 → 48 ticks per 16th-note step), so tempo
 * changes during playback rescale naturally.
 */
export const PPQ = 192;
export const TICKS_PER_STEP = PPQ / 4;

export interface ScheduledNote {
  tick: number;
  instrument: Instrument;
  pitch: number;
  durationTicks: number;
  velocity: number;
}

export interface CompiledSection {
  id: string;
  name: string;
  kind: SectionKind;
  startStep: number;
  endStep: number;
}

/**
 * One audio clip ready to play. Position is in ticks like notes; everything else is in
 * seconds. `playSec` is how long the clip sounds on the timeline (loops included, cut
 * at the end of the song).
 */
export interface ScheduledClip {
  clipId: string;
  assetId: string;
  tick: number;
  offsetSec: number;
  /** Source seconds played per pass. */
  durationSec: number;
  loops: number;
  playbackRate: number;
  reverse: boolean;
  /** Linear gain including normalisation. */
  gain: number;
  fadeInSec: number;
  fadeOutSec: number;
  playSec: number;
  filter: AudioClip['filter'];
}

export interface CompiledTimeline {
  events: ScheduledNote[];
  /** Audio clips (song mode only; empty for pattern playback). Sorted by tick. */
  clips: ScheduledClip[];
  totalSteps: number;
  totalTicks: number;
  sections: CompiledSection[];
}

export type PlaybackTarget = { mode: 'pattern'; patternId: string } | { mode: 'song' };

/** Tick position of a note, with swing on off-beat 16ths and micro-timing applied. */
export function noteTick(step: number, offset: number, swing: number): number {
  const swingShift = step % 2 === 1 ? swing * TICKS_PER_STEP : 0;
  return Math.max(0, Math.round(step * TICKS_PER_STEP + swingShift + offset * TICKS_PER_STEP));
}

/** Emits a pattern's notes, looping it to fill `steps` starting at `startStep`. */
function emitPattern(
  out: ScheduledNote[],
  pattern: Pattern,
  startStep: number,
  steps: number,
  swing: number,
): void {
  const endTick = (startStep + steps) * TICKS_PER_STEP;
  for (let rep = 0; rep * pattern.lengthSteps < steps; rep++) {
    const base = startStep + rep * pattern.lengthSteps;
    for (const [instrument, notes] of Object.entries(pattern.tracks) as [Instrument, Pattern['tracks'][Instrument]][]) {
      for (const n of notes ?? []) {
        const localStep = rep * pattern.lengthSteps + n.step;
        if (localStep >= steps) continue;
        const tick = noteTick(base + n.step, n.offset, swing);
        if (tick >= endTick) continue;
        // Notes never ring past the end of their section (no hanging notes into the next).
        const durationTicks = Math.max(1, Math.min(Math.round(n.length * TICKS_PER_STEP), endTick - tick));
        out.push({ tick, instrument, pitch: n.pitch, durationTicks, velocity: n.velocity });
      }
    }
  }
}

const byTime = (a: ScheduledNote, b: ScheduledNote) =>
  a.tick - b.tick || a.instrument.localeCompare(b.instrument) || a.pitch - b.pitch;

export function compileTimeline(project: Project, target: PlaybackTarget): CompiledTimeline {
  const swing = project.settings.swing;
  const events: ScheduledNote[] = [];
  const sections: CompiledSection[] = [];
  let totalSteps = 0;
  if (target.mode === 'pattern') {
    const pattern = project.patterns.find((p) => p.id === target.patternId) ?? project.patterns[0]!;
    emitPattern(events, pattern, 0, pattern.lengthSteps, swing);
    totalSteps = pattern.lengthSteps;
  } else {
    for (const section of project.arrangement.sections) {
      const pattern = project.patterns.find((p) => p.id === section.patternId);
      const steps = section.bars * STEPS_PER_BAR;
      if (pattern) emitPattern(events, pattern, totalSteps, steps, swing);
      sections.push({ id: section.id, name: section.name, kind: section.kind, startStep: totalSteps, endStep: totalSteps + steps });
      totalSteps += steps;
    }
  }
  events.sort(byTime);
  const clips = target.mode === 'song' ? compileClips(project, totalSteps) : [];
  return { events, clips, totalSteps, totalTicks: totalSteps * TICKS_PER_STEP, sections };
}

export const secondsPerTick = (bpm: number): number => 60 / (bpm * PPQ);

/** Peak level that "normalize" brings a clip's source to (−1 dBFS). */
export const NORMALIZE_TARGET = 10 ** (-1 / 20);

function compileClips(project: Project, totalSteps: number): ScheduledClip[] {
  const spt = secondsPerTick(project.settings.bpm);
  const endTick = totalSteps * TICKS_PER_STEP;
  const out: ScheduledClip[] = [];
  for (const clip of project.audioClips) {
    if (clip.muted) continue;
    const tick = Math.round(clip.startStep * TICKS_PER_STEP);
    if (tick >= endTick) continue;
    const fullSec = (clip.durationSec / clip.playbackRate) * clip.loops;
    const playSec = Math.min(fullSec, (endTick - tick) * spt);
    if (playSec <= 0) continue;
    const peak = project.assets.find((a) => a.id === clip.assetId)?.audio?.peak ?? 0;
    const normalize = clip.normalize && peak > 0.0001 ? NORMALIZE_TARGET / peak : 1;
    out.push({
      clipId: clip.id,
      assetId: clip.assetId,
      tick,
      offsetSec: clip.offsetSec,
      durationSec: clip.durationSec,
      loops: clip.loops,
      playbackRate: clip.playbackRate,
      reverse: clip.reverse,
      gain: (clip.gainDb <= -48 ? 0 : 10 ** (clip.gainDb / 20)) * normalize,
      fadeInSec: clip.fadeInSec,
      fadeOutSec: clip.fadeOutSec,
      playSec,
      filter: clip.filter,
    });
  }
  return out.sort((a, b) => a.tick - b.tick || a.clipId.localeCompare(b.clipId));
}

/** Assets (and reversed variants) that playing this timeline needs decoded. */
export function requiredAudio(project: Project, timeline: CompiledTimeline): { assetId: string; reversed: boolean }[] {
  const wanted = new Map<string, { assetId: string; reversed: boolean }>();
  const add = (assetId: string, reversed: boolean) => wanted.set(`${assetId}:${reversed}`, { assetId, reversed });
  const playing = new Set(timeline.events.map((e) => e.instrument));
  for (const track of project.tracks) {
    if (track.sample && playing.has(track.instrument)) add(track.sample.assetId, track.sample.reverse);
  }
  for (const clip of timeline.clips) add(clip.assetId, clip.reverse);
  return [...wanted.values()];
}

/** Length of the song (arrangement) in seconds at the project tempo. */
export function songDurationSeconds(project: Project): number {
  const steps = project.arrangement.sections.reduce((sum, s) => sum + s.bars * STEPS_PER_BAR, 0);
  return steps * TICKS_PER_STEP * secondsPerTick(project.settings.bpm);
}

/** Which section contains a step (for the playhead), or undefined. */
export function sectionAtStep(timeline: CompiledTimeline, step: number): CompiledSection | undefined {
  return timeline.sections.find((s) => step >= s.startStep && step < s.endStep);
}
